import React, { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { FormattedMessage } from '@atlassian/jira-intl';
import { FormStatus } from '@atlassian/proforma-common-core/form-system-models';
import type { FormStore } from '@atlassian/proforma-common-core/form-system-stores';
import {
	type FormIndexItem,
	useFormIndexActions,
	useFormIndexState,
} from '@atlassian/proforma-form-system';
import { Action, ActionSubject, FormUpdateType } from '@atlassian/proforma-utils/analytics';
import { useShowNotice } from '@atlassian/proforma-utils/show-notice';
import type { ProjectType } from '@atlassian/proforma-utils/types';
import { messages } from './messages.tsx';
import { type HandleOpenForm, OpenFormResult } from './types.tsx';

export const useHandleReopenFormWithIndexItem = (projectType: ProjectType): HandleOpenForm => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { updateFormStatus, reopenForm, reportError } = useFormIndexActions();
	const { permissions } = useFormIndexState();
	const { showNotice } = useShowNotice();

	const confirmReopenForm = useCallback(
		(formIndexItem: FormIndexItem, formStore?: FormStore) =>
			reopenForm(formIndexItem.id, formIndexItem.status).then((updatedForm) => {
				if (formStore) {
					return formStore.update(updatedForm, true).then((_) => {
						updateFormStatus(updatedForm.id, updatedForm.state.status);
					});
				}
				updateFormStatus(updatedForm.id, updatedForm.state.status);
				return Promise.resolve();
			}),
		[reopenForm, updateFormStatus],
	);

	return useCallback(
		async (formIndexItem: FormIndexItem, formStore?: FormStore): Promise<OpenFormResult> => {
			if (
				!permissions.reopen ||
				(formIndexItem.status === FormStatus.Locked && !permissions.unlock)
			) {
				return Promise.resolve(OpenFormResult.Failure);
			}
			if (!formIndexItem.id) {
				return Promise.resolve(OpenFormResult.Failure);
			}
			return new Promise((resolve) => {
				showNotice({
					title:
						projectType === 'service_desk' ? (
							<FormattedMessage {...messages.confirmReopenJsmFormNoticeTitle} />
						) : (
							<FormattedMessage {...messages.confirmReopenFormNoticeTitle} />
						),
					body:
						projectType === 'service_desk' ? (
							<FormattedMessage {...messages.confirmReopenJsmFormNoticeBody} />
						) : (
							<FormattedMessage {...messages.confirmReopenNonJsmFormNoticeBody} />
						),
					closeText: <FormattedMessage {...messages.noticeCloseLabel} />,
					onCloseHandler: () => resolve(OpenFormResult.Cancelled),
					confirmText: <FormattedMessage {...messages.noticeConfirmLabel} />,
					onConfirmHandler: () =>
						confirmReopenForm(formIndexItem, formStore)
							.then(() => {
								fireTrackAnalytics(
									createAnalyticsEvent({}),
									`${ActionSubject.FORM} ${Action.UPDATED}`,
									{
										formId: formIndexItem.id,
										templateFormId: formIndexItem.projectFormId,
										formUpdateType: FormUpdateType.REOPEN,
										visibility: formIndexItem.internal ? 'internal' : 'external',
									},
								);
								return resolve(OpenFormResult.Success);
							})
							.catch((error) => {
								reportError(error, {
									id: 'reopenForm',
									attributes: { formId: formIndexItem.id },
								});
								return resolve(OpenFormResult.Failure);
							}),
				});
			});
		},
		[
			permissions.reopen,
			permissions.unlock,
			showNotice,
			projectType,
			confirmReopenForm,
			createAnalyticsEvent,
			reportError,
		],
	);
};
